import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';
import {
  containerItemSchema,
  type ContainerItemFormData,
} from '../schemas/import-tracking.schemas';
import { useImportTrackingStore } from '../store/import-tracking-store';
import { CONTAINER_COLORS } from '../utils/container-colors';
import type { ContainerItem } from '../types/import-tracking.types';

interface ContainerItemFormDialogProps {
  containerId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item?: ContainerItem;
  onSuccess?: () => void;
}

export function ContainerItemFormDialog({
  containerId,
  open,
  onOpenChange,
  item,
  onSuccess,
}: ContainerItemFormDialogProps) {
  const { addContainerItem, updateContainerItem } = useImportTrackingStore();
  const isEdit = !!item;

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<ContainerItemFormData>({
    resolver: zodResolver(containerItemSchema),
    defaultValues: {
      description: '',
      lengthCm: 0,
      widthCm: 0,
      heightCm: 0,
      weightKg: 0,
      quantity: 1,
      color: CONTAINER_COLORS[0],
    },
  });

  const selectedColor = watch('color');

  useEffect(() => {
    if (open) {
      if (item) {
        reset({
          materialId: item.materialId || '',
          description: item.description,
          lengthCm: item.lengthCm,
          widthCm: item.widthCm,
          heightCm: item.heightCm,
          weightKg: item.weightKg,
          quantity: item.quantity,
          color: item.color || CONTAINER_COLORS[0],
        });
      } else {
        reset({
          description: '',
          lengthCm: 0,
          widthCm: 0,
          heightCm: 0,
          weightKg: 0,
          quantity: 1,
          color: CONTAINER_COLORS[Math.floor(Math.random() * CONTAINER_COLORS.length)],
        });
      }
    }
  }, [open, item, reset]);

  const onSubmit = async (data: ContainerItemFormData) => {
    try {
      if (isEdit && item) {
        await updateContainerItem(containerId, item.id, data);
      } else {
        await addContainerItem(containerId, data);
      }
      onOpenChange(false);
      onSuccess?.();
    } catch {
      // Error is handled by the store
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Editar Item' : 'Agregar Item al Contenedor'}</DialogTitle>
          <DialogDescription>
            {isEdit ? 'Modifique los datos del item' : 'Ingrese las dimensiones y datos del item'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <Label htmlFor="description">Descripción *</Label>
            <Input
              id="description"
              {...register('description')}
              placeholder="Caja de productos..."
            />
            {errors.description && (
              <p className="text-xs text-destructive mt-1">{errors.description.message}</p>
            )}
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label htmlFor="lengthCm">Largo (cm) *</Label>
              <Input id="lengthCm" type="number" step="0.1" {...register('lengthCm')} />
              {errors.lengthCm && (
                <p className="text-xs text-destructive mt-1">{errors.lengthCm.message}</p>
              )}
            </div>
            <div>
              <Label htmlFor="widthCm">Ancho (cm) *</Label>
              <Input id="widthCm" type="number" step="0.1" {...register('widthCm')} />
              {errors.widthCm && (
                <p className="text-xs text-destructive mt-1">{errors.widthCm.message}</p>
              )}
            </div>
            <div>
              <Label htmlFor="heightCm">Alto (cm) *</Label>
              <Input id="heightCm" type="number" step="0.1" {...register('heightCm')} />
              {errors.heightCm && (
                <p className="text-xs text-destructive mt-1">{errors.heightCm.message}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="weightKg">Peso (kg) *</Label>
              <Input id="weightKg" type="number" step="0.1" {...register('weightKg')} />
              {errors.weightKg && (
                <p className="text-xs text-destructive mt-1">{errors.weightKg.message}</p>
              )}
            </div>
            <div>
              <Label htmlFor="quantity">Cantidad *</Label>
              <Input id="quantity" type="number" min="1" {...register('quantity')} />
              {errors.quantity && (
                <p className="text-xs text-destructive mt-1">{errors.quantity.message}</p>
              )}
            </div>
          </div>

          <div>
            <Label>Color</Label>
            <div className="flex flex-wrap gap-2 mt-1">
              {CONTAINER_COLORS.map((color) => (
                <button
                  key={color}
                  type="button"
                  className={`w-7 h-7 rounded-full border-2 transition-all ${
                    selectedColor === color
                      ? 'border-foreground scale-110'
                      : 'border-transparent hover:border-muted-foreground/50'
                  }`}
                  style={{ backgroundColor: color }}
                  onClick={() => setValue('color', color)}
                />
              ))}
            </div>
          </div>

          <div>
            <Label htmlFor="materialId">ID Material (opcional)</Label>
            <Input id="materialId" {...register('materialId')} placeholder="ID del material..." />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {isEdit ? 'Guardar Cambios' : 'Agregar Item'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
